import { useState } from 'react';
import { api, fmtDateTime, fmtCurrency } from './SecretaryPortal';

// ─── Financial secretary's refund-request decide dialog ──────────────────────
//
// Opened by clicking a refund-request row in RefundRequestsList (the
// financial secretary's own queue, mirroring the entry secretary's "New
// Entries Received" inbox). Two terminal actions, both closing out the
// request: Mark Sent (money actually paid out) or Decline (reviewed and
// decided nothing is owed). Mirrors SubmissionActionDialog's modal +
// confirm-card shell.

function riderName(submission) {
  const r = submission.payload?.rider || {};
  return [r.firstName, r.surname].filter(Boolean).join(' ') || 'this rider';
}

export default function RefundActionDialog({ submission, event, sessionToken, onClose, onActioned }) {
  const alreadySent = submission.refund_amount || 0;
  const requestedNow = submission.refund_requested_amount ?? submission.refund_amount ?? '';
  const [amountOverride, setAmountOverride] = useState(String(requestedNow ?? ''));
  const [declineReason, setDeclineReason]   = useState('');
  const [confirming, setConfirming]         = useState(null); // null | 'sent' | 'decline'
  const [submitting, setSubmitting]         = useState(false);
  const [submitError, setSubmitError]       = useState(null);

  const doSent = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const amount = parseFloat(amountOverride);
      await api(
        `/api/secretary/entries/${event.CalendarID}/submissions/${submission.id}/refund-sent`,
        { method: 'PUT', body: JSON.stringify(Number.isFinite(amount) ? { amount } : {}) },
        sessionToken
      );
      onActioned();
      onClose();
    } catch (err) {
      setConfirming(null);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const doDecline = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api(
        `/api/secretary/entries/${event.CalendarID}/submissions/${submission.id}/refund-decline`,
        { method: 'PUT', body: JSON.stringify({ reason: declineReason }) },
        sessionToken
      );
      onActioned();
      onClose();
    } catch (err) {
      setConfirming(null);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sec-overlay">
      <div className="sec-modal" style={{ maxWidth: '520px' }}>
        <div className="sec-modal-header">
          <h3>Refund Request — {riderName(submission)}</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="sec-modal-body">
          <div style={{ background: 'var(--bg-color)', borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem' }}>
            <p style={{ marginBottom: '0.3rem' }}>
              <strong>Requesting now:</strong> {fmtCurrency(submission.refund_requested_amount ?? submission.refund_amount)}
            </p>
            {alreadySent > 0 && (
              <p style={{ marginBottom: '0.3rem' }}>
                <strong>Already sent:</strong> {fmtCurrency(alreadySent)}
              </p>
            )}
            <p style={{ marginBottom: '0.3rem' }}>
              <strong>Note:</strong> {submission.refund_note || '—'}
            </p>
            <p style={{ marginBottom: '0.3rem' }}>
              <strong>Requested by:</strong> {submission.refund_requested_by || '—'}
              {submission.refund_requested_at ? ` on ${fmtDateTime(submission.refund_requested_at)}` : ''}
            </p>
            <p>
              <strong>Submission status:</strong> {submission.status}
            </p>
          </div>

          <div className="form-group">
            <label>Amount to send now (£)</label>
            <input type="number" step="0.01" min="0" value={amountOverride}
                   onChange={e => setAmountOverride(e.target.value)} />
          </div>

          {submitError && (
            <div className="error-message" style={{ marginTop: '0.5rem' }}>
              <p>⚠️ {submitError}</p>
              <button onClick={() => setSubmitError(null)}>Dismiss</button>
            </div>
          )}
        </div>

        <div className="sec-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-delete" onClick={() => setConfirming('decline')}>Decline</button>
          <button className="btn-save" onClick={() => setConfirming('sent')}>Mark Sent</button>
        </div>
      </div>

      {confirming === 'sent' && (
        <div className="confirm-overlay">
          <div className="confirm-card">
            <h3>Confirm Refund Sent</h3>
            <p>
              This will mark {fmtCurrency(parseFloat(amountOverride) || requestedNow)} as sent to {riderName(submission)}
              {alreadySent > 0 && ` (bringing the total sent to ${fmtCurrency(alreadySent + (parseFloat(amountOverride) || 0))})`}.
              This cannot be undone from here.
            </p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setConfirming(null)} disabled={submitting}>Cancel</button>
              <button className="btn-confirm-delete" onClick={doSent} disabled={submitting}>
                {submitting ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirming === 'decline' && (
        <div className="confirm-overlay">
          <div className="confirm-card">
            <h3>Decline Refund Request</h3>
            <p>Reviewed and decided nothing is owed to {riderName(submission)}. Give a reason for the record:</p>
            <div className="form-group">
              <textarea rows={3} value={declineReason} onChange={e => setDeclineReason(e.target.value)}
                        style={{ resize: 'vertical', width: '100%' }} placeholder="e.g. Payment matches fee exactly" />
            </div>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setConfirming(null)} disabled={submitting}>Cancel</button>
              <button className="btn-confirm-delete" onClick={doDecline} disabled={submitting || !declineReason.trim()}>
                {submitting ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
